import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

type RuntimeConfig = {
  sourceResolverBasePath: string;
  sourceWorkdirPath: string;
};

type RuntimeConfigValue = {
  config: RuntimeConfig;
  loaded: boolean;
};

const RuntimeConfigContext = createContext<RuntimeConfigValue | undefined>(undefined);

export const useRuntimeConfigContext = () => {
  const ctx = useContext(RuntimeConfigContext);
  if (!ctx) throw new Error('useRuntimeConfigContext must be used within RuntimeConfigProvider');
  return ctx;
};

export const RuntimeConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [config, setConfig] = useState<RuntimeConfig>({ sourceResolverBasePath: '', sourceWorkdirPath: '' });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const base = process.env.REACT_APP_API_BASE || '';
        const resp = await fetch(`${base}/api/config`);
        if (!active) return;
        if (resp.ok) {
          const json = await resp.json();
          setConfig({ sourceResolverBasePath: json?.sourceResolverBasePath || '', sourceWorkdirPath: json?.sourceWorkdirPath || '' });
        }
      } catch (_e) {
        // ignore
      } finally {
        if (active) setLoaded(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const value = useMemo(() => ({ config, loaded }), [config, loaded]);
  return (
    <RuntimeConfigContext.Provider value={value}>
      {children}
    </RuntimeConfigContext.Provider>
  );
};


